import format from 'date-fns/format';
import { fromUnixTime } from 'date-fns';

import { t } from '$translation/helper';

import type { IncomeType } from '../StatisticsPro.types';

export interface CsvRow {
  date: number;
  values: Partial<Record<IncomeType, number>>;
}

export interface CsvExportOptions {
  rows: CsvRow[];
  categories: IncomeType[];
  othersTypes?: IncomeType[];
  labels: Record<string, string>;
  filename: string;
}

const escapeCell = (value: string): string => {
  if (/[",\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

export function buildCsv({
  rows,
  categories,
  othersTypes = [],
  labels,
}: Omit<CsvExportOptions, 'filename'>): string {
  const hasOthers = othersTypes.length > 0;
  const othersLabel = t(
    categories.length === 0 ? 'statistics_pro_all' : 'statistics_pro_others',
  );

  const header = [
    t('statistics_pro_col_date'),
    ...categories.map((c) => labels[c] ?? c),
    ...(hasOthers ? [othersLabel] : []),
    t('statistics_pro_total'),
  ];
  const lines = [header.map(escapeCell).join(',')];

  for (const row of rows) {
    const dateStr = format(fromUnixTime(row.date), 'yyyy-MM-dd');
    const cells: string[] = [dateStr];
    let total = 0;
    for (const cat of categories) {
      const v = row.values[cat] ?? 0;
      total += v;
      cells.push(String(Math.round(v * 100) / 100));
    }
    let othersValue = 0;
    if (hasOthers) {
      for (const t of othersTypes) {
        othersValue += row.values[t] ?? 0;
      }
      total += othersValue;
    }
    if (total === 0) {
      continue;
    }
    if (hasOthers) {
      cells.push(String(Math.round(othersValue * 100) / 100));
    }
    cells.push(String(Math.round(total * 100) / 100));
    lines.push(cells.map(escapeCell).join(','));
  }

  return lines.join('\n');
}

export function downloadCsv(options: CsvExportOptions): void {
  const csv = buildCsv(options);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = options.filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
